/**
 * High-level profession categories (French labels).
 * Keep codes in sync with backend/src/constants/professions.js.
 */

export const PROFESSION_CODES = [
  'etudiant',
  'enseignant',
  'sante',
  'fonction_publique',
  'commerce',
  'artisanat',
  'agriculture',
  'transport',
  'btp',
  'industrie',
  'technologies',
  'finance',
  'juridique',
  'communication',
  'services',
  'securite',
  'religieux',
  'retraite',
  'sans_emploi',
  'autre',
];

export const PROFESSION_LABELS_FR = {
  etudiant: 'Étudiant / Élève',
  enseignant: 'Enseignant / Formateur',
  sante: 'Santé (médecin, infirmier, sage-femme…)',
  fonction_publique: 'Fonction publique / Administration',
  commerce: 'Commerce / Vente',
  artisanat: 'Artisanat / Métiers manuels',
  agriculture: 'Agriculture / Élevage / Pêche',
  transport: 'Transport / Logistique',
  btp: 'Bâtiment / Travaux publics',
  industrie: 'Industrie / Production',
  technologies: 'Technologies / Numérique',
  finance: 'Banque / Finance / Assurance',
  juridique: 'Droit / Juridique',
  communication: 'Communication / Médias / Marketing',
  services: 'Services (hôtellerie, restauration, beauté…)',
  securite: 'Sécurité / Défense',
  religieux: 'Religieux / Associatif / ONG',
  retraite: 'Retraité',
  sans_emploi: 'Sans emploi / En recherche',
  autre: 'Autre',
};

export const PROFESSION_UNSET_KEY = '_unset';
export const PROFESSION_UNSET_LABEL_FR = 'Non renseigné';

/**
 * @param {string|null|undefined} code
 * @returns {string}
 */
export function professionLabelFr(code) {
  if (code == null || code === '' || code === PROFESSION_UNSET_KEY) {
    return PROFESSION_UNSET_LABEL_FR;
  }
  return PROFESSION_LABELS_FR[code] || code;
}

/**
 * Options for <select>: { value, label }[]
 */
export function professionOptions() {
  return PROFESSION_CODES.map((value) => ({
    value,
    label: PROFESSION_LABELS_FR[value],
  }));
}

/**
 * Sort by_profession map entries for display (highest count first).
 * @param {Record<string, number>|null|undefined} byProfession
 * @returns {Array<{ code: string, label: string, count: number }>}
 */
export function professionBreakdownRows(byProfession) {
  if (!byProfession || typeof byProfession !== 'object') return [];
  return Object.entries(byProfession)
    .map(([code, count]) => ({
      code,
      label: professionLabelFr(code),
      count: Number(count) || 0,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'fr'));
}

const USER_TYPE_LABELS_SHORT = {
  parrain_grand: 'PGE',
  parrain_sympa: 'P. Sympa',
  relais: 'Relais',
  beneficiaire: 'Bénéf.',
  gouverneur: 'Gouv.',
  admin: 'Admin',
};

/**
 * Flatten by_profession_and_type for tables/CSV.
 * @param {Record<string, Record<string, number>>|null|undefined} matrix
 * @returns {Array<{ profession: string, professionLabel: string, type: string, typeLabel: string, count: number }>}
 */
export function professionByTypeRows(matrix) {
  if (!matrix || typeof matrix !== 'object') return [];
  const rows = [];
  for (const [profession, byType] of Object.entries(matrix)) {
    if (!byType || typeof byType !== 'object') continue;
    for (const [type, count] of Object.entries(byType)) {
      rows.push({
        profession,
        professionLabel: professionLabelFr(profession),
        type,
        typeLabel: USER_TYPE_LABELS_SHORT[type] || type,
        count: Number(count) || 0,
      });
    }
  }
  return rows.sort(
    (a, b) =>
      b.count - a.count ||
      a.professionLabel.localeCompare(b.professionLabel, 'fr') ||
      a.typeLabel.localeCompare(b.typeLabel, 'fr')
  );
}
